import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ImageTable from '../visualization/ImageTable';
import { getImageKey } from '../visualization/imageCsv';
import ImportDatesButton from '../acquisition/ImportDatesButton';
import StepperButtons from '../acquisition/StepperButtons';
import { Actions as Acquisition } from '../../../store/ducks/acquisition';
import TourGuider from '../tour/TourGuider';
import { useLocalStorage } from '../../../common/utils';
import { Box, Button } from '@material-ui/core';
import HelpButton from '../core/HelpButton';

/**
 * @function ImageListRefiner
 * @description Fourth step of the intertidal acquisition wizard: displays all images available
 * in the chosen date range and lets the user select which ones to carry forward for acquisition.
 * @param {Object} props - Component properties.
 * @param {Function} props.navigate - Step navigation handler, forwarded to StepperButtons.
 * @see {@link handleChoose} - commits the selected images on "Next".
 * **Called by:
 * @see {IntertidalPage} - as the fourth acquisition step.
 * @memberof module:INTERTIDAL
 * @returns {JSX.Element} The image selection table, tour guide, and stepper controls.
 */
const ImageListRefiner = ({ navigate }) => {
  const metadata = useSelector((state) => state.acquisition.metadata);
  const dates = useSelector((state) => state.acquisition.availableDates);

  const dispatch = useDispatch();
  const [t] = useTranslation();

  const steps = [
    { selector: '#column0', content: t('tour.acquisition.4.id') },
    { selector: '#column1', content: t('tour.acquisition.4.clouds') },
    { selector: '#column2', content: t('tour.acquisition.4.image') },
    { selector: '#columnaction', content: t('tour.acquisition.4.action') },
  ];

  const [isTourOpen, setIsTourOpen] = useLocalStorage('showImageListChooserTour', true);

  const [selected, setSelected] = useState(() => {
    try {
      return JSON.parse(window.sessionStorage.getItem('intertidalImageSelection') || '{}');
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (!Array.isArray(dates) || dates.length === 0) {
      setSelected({});
      return;
    }

    setSelected((prev) => {
      const next = { ...prev };

      dates.forEach((img) => {
        const key = getImageKey(img);
        if (next[key] === undefined) next[key] = true;
      });

      const validKeys = new Set(dates.map(getImageKey));
      Object.keys(next).forEach((k) => {
        if (!validKeys.has(k)) delete next[k];
      });

      return next;
    });
  }, [dates]);

  useEffect(() => {
    window.sessionStorage.setItem('intertidalImageSelection', JSON.stringify(selected));
  }, [selected]);

  /**
   * @function handleChange
   * @description Updates a single image's selection state.
   * @param {String} key - Image key, as produced by getImageKey.
   * @param {boolean} checked - Whether the image is selected.
   * **Called by:
   * @see {ImageListRefiner} - via ImageTable's onCheckboxChange prop.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleChange = (key, checked) => {
    setSelected((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  /**
   * @function handleSelectAll
   * @description Sets the selection state of every given image key to the same value.
   * @param {Array<String>} keys - Image keys to update.
   * @param {boolean} checked - Whether the images should be selected.
   * **Called by:
   * @see {ImageListRefiner} - via ImageTable's onSelectAll prop.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleSelectAll = (keys, checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      keys.forEach((k) => {
        next[k] = checked;
      });
      return next;
    });
  };

  /**
   * @function handleChoose
   * @description Filters the available dates down to the selected images and commits them to
   * the acquisition state.
   * **Called by:
   * @see {ImageListRefiner} - via StepperButtons' onNext.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleChoose = () => {
    if (!Array.isArray(dates)) return;

    try {
      const existing = JSON.parse(window.sessionStorage.getItem('intertidalFullImageList') || 'null');
      if (!existing || dates.length >= existing.length) {
        window.sessionStorage.setItem('intertidalFullImageList', JSON.stringify(dates));
      }
    } catch {
      window.sessionStorage.setItem('intertidalFullImageList', JSON.stringify(dates));
    }

    const filtered = dates.filter((image) => {
      const key = getImageKey(image);
      return !!selected[key];
    });

    dispatch(Acquisition.setAvailableDates(filtered));
  };

  /**
   * @function handleReloadFullList
   * @description Restores the full candidate list backed up by handleChoose (see above), so the
   * user can get back every image found for their original AOI/period/cloud filters after having
   * narrowed it down to a selection — without navigating back and redoing the search. Previously
   * checked images stay checked, since selection is tracked separately, by key.
   * **Called by:
   * @see {ImageListRefiner} - via the "reload full list" button's onClick.
   * @memberof module:INTERTIDAL
   * @returns {void}
   */
  const handleReloadFullList = () => {
    try {
      const backup = JSON.parse(window.sessionStorage.getItem('intertidalFullImageList') || 'null');
      if (Array.isArray(backup) && backup.length) {
        dispatch(Acquisition.setAvailableDates(backup));
      }
    } catch {}
  };

  let fullListBackup = null;
  try {
    fullListBackup = JSON.parse(window.sessionStorage.getItem('intertidalFullImageList') || 'null');
  } catch {
    fullListBackup = null;
  }
  const canReloadFullList = Boolean(fullListBackup?.length > (dates?.length || 0));

  useEffect(() => {
    if (!Array.isArray(dates) || dates.length === 0) return undefined;
    const handle = setTimeout(() => {
      dispatch(Acquisition.loadThumbnails(dates));
    }, 400);
    return () => {
      clearTimeout(handle);
      dispatch(Acquisition.cancelThumbnails());
    };
  }, [dates, dispatch]);

  return (
    <Box>
      <HelpButton
        onClickFunction={() => {
          setIsTourOpen(true);
        }}
      />

      {canReloadFullList && (
        <Box mb={2} display="flex" justifyContent="center">
          <Button variant="outlined" color="primary" onClick={handleReloadFullList}>
            {t('forms.acquisition.4.reloadFullList')}
          </Button>
        </Box>
      )}

      <Box mx={2} mb={1}>
        <ImportDatesButton images={dates} onImport={setSelected} />
      </Box>

      <div>
        <ImageTable
          metadata={metadata}
          images={dates}
          selected={selected}
          onCheckboxChange={handleChange}
          onSelectAll={handleSelectAll}
          variant="modern"
        />

        <StepperButtons navigate={navigate} onNext={handleChoose} />

        <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
      </div>
    </Box>
  );
};

export default ImageListRefiner;
